import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { AXIS_LABELS } from "../../model/AXIS_LABELS";
import type { DuelLine } from "../../model/duel";
import type { StatAxis } from "../../model/FriendsBoard";

const WIDTH = 360;
const HEIGHT = 300;
const CENTER_X = WIDTH / 2;
const CENTER_Y = HEIGHT / 2;
const RADIUS = 145;
// Оценка 0 — не в центре, а чуть от него: иначе нулевые стороны сливаются в точку.
const FLOOR = 0.1;
const RINGS = [0.4, 0.7, 1];

const LABEL = msg({ message: "Sides of the game: {sides}" });
const SIDE = msg({ message: "{side} {me} vs {them}" });

// Угол стороны: квадрат повёрнут углом вверх, стороны — по часовой от верхнего левого.
const point = (index: number, share: number): [number, number] => {
  const angle = (-3 * Math.PI) / 4 + (index * Math.PI) / 2;
  return [CENTER_X + RADIUS * share * Math.cos(angle), CENTER_Y + RADIUS * share * Math.sin(angle)];
};

const share = (score: number | null) => FLOOR + ((1 - FLOOR) * (score ?? 0)) / 100;

const polygon = (shares: readonly number[]) =>
  shares
    .map((value, index) =>
      point(index, value)
        .map((coordinate) => coordinate.toFixed(1))
        .join(","),
    )
    .join(" ");

type SidesSquareProps = {
  sides: readonly DuelLine<StatAxis>[];
  // Сторона под мышью или в фокусе в строках сравнения: подсвечивается и здесь.
  hovered: StatAxis | null;
  onHover: (axis: StatAxis | null) => void;
};

// Квадрат сторон игры: ты (accent) и друг (rival) по четырём оценкам 0–99.
export const SidesSquare = ({ sides, hovered, onHover }: SidesSquareProps) => {
  const { i18n } = useLingui();
  const text = (score: number | null) => (score === null ? "—" : String(score));
  const label = i18n._({
    ...LABEL,
    values: {
      sides: sides
        .map((side) =>
          i18n._({
            ...SIDE,
            values: {
              side: i18n._(AXIS_LABELS[side.key]),
              me: text(side.me),
              them: text(side.them),
            },
          }),
        )
        .join(", "),
    },
  });
  const active = sides.findIndex((side) => side.key === hovered);
  const activeSide = sides[active];
  return (
    <div className="relative mx-auto aspect-[360/300] w-full max-w-[380px]">
      <svg
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        role="img"
        aria-label={label}
        className="absolute inset-0 size-full"
      >
        {RINGS.map((ring) => (
          <polygon
            key={ring}
            points={polygon(sides.map(() => ring))}
            className={ring === 1 ? "fill-surface stroke-border" : "fill-none stroke-border"}
          />
        ))}
        {sides.map((side, index) => {
          const [x, y] = point(index, 1);
          return (
            <line
              key={side.key}
              x1={CENTER_X}
              y1={CENTER_Y}
              x2={x}
              y2={y}
              className="stroke-border"
            />
          );
        })}
        <polygon
          points={polygon(sides.map((side) => share(side.them)))}
          className="fill-rival/20 stroke-rival"
          strokeWidth={2}
          strokeLinejoin="round"
        />
        <polygon
          points={polygon(sides.map((side) => share(side.me)))}
          className="fill-accent/25 stroke-accent"
          strokeWidth={2}
          strokeLinejoin="round"
        />
        {activeSide !== undefined && (
          <g>
            <line
              x1={CENTER_X}
              y1={CENTER_Y}
              x2={point(active, 1)[0]}
              y2={point(active, 1)[1]}
              className="stroke-foreground"
              strokeWidth={2}
              strokeDasharray="4 4"
            />
            <circle
              cx={point(active, share(activeSide.them))[0]}
              cy={point(active, share(activeSide.them))[1]}
              r={7}
              className="fill-rival stroke-background"
              strokeWidth={2}
            />
            <circle
              cx={point(active, share(activeSide.me))[0]}
              cy={point(active, share(activeSide.me))[1]}
              r={7}
              className="fill-accent stroke-background"
              strokeWidth={2}
            />
          </g>
        )}
      </svg>
      {sides.map((side, index) => {
        const [x, y] = point(index, 1);
        const top = index < 2;
        return (
          <span
            key={side.key}
            aria-hidden="true"
            onMouseEnter={() => {
              onHover(side.key);
            }}
            onMouseLeave={() => {
              onHover(null);
            }}
            style={{
              left: `${String((x / WIDTH) * 100)}%`,
              top: `${String(((top ? y - 8 : y + 8) / HEIGHT) * 100)}%`,
              transform: top ? "translate(-50%, -100%)" : "translate(-50%, 0)",
            }}
            className={`absolute flex flex-col items-center rounded-md px-1.5 py-1 text-center leading-tight transition-opacity ${
              hovered === side.key
                ? "bg-sidebar-active ring-1 ring-border-strong"
                : hovered === null
                  ? ""
                  : "opacity-45"
            }`}
          >
            <span className="text-[13px] font-semibold whitespace-nowrap">
              {i18n._(AXIS_LABELS[side.key])}
            </span>
            <span className="font-mono text-xs whitespace-nowrap">
              <span className="text-accent">{text(side.me)}</span>
              <span className="text-neutral"> / </span>
              <span className="text-rival">{text(side.them)}</span>
            </span>
          </span>
        );
      })}
    </div>
  );
};
